import { useState } from "react";
import type { DateRange } from "react-day-picker";
import {
    Calendar,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
    Text,
} from "@athena/ui";

export type TimeFrameMode = "MONTH" | "RANGE" | "ACADEMIC_YEAR";

export interface TimeFrame {
    mode: TimeFrameMode;
    from: Date;
    to: Date;
    label: string;
}

/** The widest range the picker allows — ~6 months (365 / 2, rounded down). Comfortably under the MAX_WINDOW_DAYS cap in userAction.ts. */
const MAX_RANGE_DAYS = 182;

const MONTH_NAMES = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

/** A few years back through the current year — there's no org-creation-date lookup to bound this precisely. */
const yearOptions = (yearsBack = 3): number[] => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: yearsBack + 1 }, (_, i) => currentYear - yearsBack + i);
};

// Built from numeric Date args (local time), never a date string — a string like
// "2026-08-01" parses as UTC midnight and shifts a day earlier in negative-UTC-offset
// timezones once rendered locally.
const startOfMonth = (year: number, month: number): Date => new Date(year, month, 1, 0, 0, 0, 0);
const endOfMonth = (year: number, month: number): Date => new Date(year, month + 1, 0, 23, 59, 59, 999);
const startOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
const endOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

const daysBetween = (from: Date, to: Date): number =>
    Math.round((to.getTime() - from.getTime()) / (24 * 60 * 60 * 1000));

/** Returns an error message if the range exceeds MAX_RANGE_DAYS, undefined if it's fine. */
export const rangeValidationError = (from: Date, to: Date): string | undefined => {
    const span = daysBetween(from, to);
    if (span <= MAX_RANGE_DAYS) return undefined;
    return `That's ${span} days — the range picker maxes out at ${MAX_RANGE_DAYS}.`;
};

export const monthTimeFrame = (year: number, month: number): TimeFrame => ({
    mode: "MONTH",
    from: startOfMonth(year, month),
    to: endOfMonth(year, month),
    label: new Date(year, month, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" }),
});

/** Fixed Sep 1 → Jun 30 approximation — there's no per-school academic calendar in the data model. */
export const academicYearTimeFrame = (startYear: number): TimeFrame => ({
    mode: "ACADEMIC_YEAR",
    from: new Date(startYear, 8, 1, 0, 0, 0, 0),
    to: new Date(startYear + 1, 5, 30, 23, 59, 59, 999),
    label: `${startYear}–${startYear + 1} academic year`,
});

export const rangeTimeFrame = (from: Date, to: Date): TimeFrame => ({
    mode: "RANGE",
    from: startOfDay(from),
    to: endOfDay(to),
    label: `${from.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${to.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`,
});

/**
 * The window to query, as UTC day boundaries.
 *
 * The stats aggregates bucket with `date_trunc('day', action_date_time)`, and that column is a
 * `timestamp without time zone` holding UTC — so report days are UTC days. A frame built from
 * local midnight then straddles them: for an admin at UTC-7, "August" ran from Aug 1 07:00Z to
 * Sep 1 06:59Z, which returned a stray `2026-09-01` month bucket and made the first and last
 * days partial.
 *
 * So the calendar date the operator picked is reinterpreted as a UTC day rather than converted
 * to one. Picking August asks for Aug 1 00:00Z – Aug 31 23:59:59.999Z: window and buckets agree,
 * and no day is half-counted.
 *
 * Deliberately not done by changing the boundaries in the frame itself — `from`/`to` are what the
 * range calendar highlights and what the labels read, and a UTC midnight renders as the previous
 * day west of Greenwich (the hazard the comment above startOfMonth describes).
 */
export const utcWindow = (frame: TimeFrame): { from: string; to: string } => ({
    from: new Date(
        Date.UTC(frame.from.getFullYear(), frame.from.getMonth(), frame.from.getDate(), 0, 0, 0, 0),
    ).toISOString(),
    to: new Date(
        Date.UTC(frame.to.getFullYear(), frame.to.getMonth(), frame.to.getDate(), 23, 59, 59, 999),
    ).toISOString(),
});

export const defaultTimeFrame = (): TimeFrame => {
    const now = new Date();
    return monthTimeFrame(now.getFullYear(), now.getMonth());
};

interface TimeFrameSelectorProps {
    value: TimeFrame;
    onChange: (next: TimeFrame) => void;
}

/**
 * Month / custom range / academic year, all reduced to the same {from, to} shape the
 * stats queries already accept. Browsing a tab and *applying* a time frame are
 * deliberately separate: switching to the range tab just shows the calendar — nothing
 * changes until two dates are actually picked, so `value` never goes stale mid-pick.
 */
export function TimeFrameSelector({ value, onChange }: TimeFrameSelectorProps) {
    const [
        viewMode,
        setViewMode,
    ] = useState<TimeFrameMode>(value.mode);
    const [
        month,
        setMonth,
    ] = useState(value.mode === "MONTH" ? value.from.getMonth() : new Date().getMonth());
    const [
        year,
        setYear,
    ] = useState(value.mode === "MONTH" ? value.from.getFullYear() : new Date().getFullYear());
    const [
        academicYearStart,
        setAcademicYearStart,
    ] = useState(value.mode === "ACADEMIC_YEAR" ? value.from.getFullYear() : new Date().getFullYear());
    const [
        range,
        setRange,
    ] = useState<DateRange | undefined>(value.mode === "RANGE" ? { from: value.from, to: value.to } : undefined);
    const [
        rangeError,
        setRangeError,
    ] = useState<string | undefined>(undefined);

    const handleTabChange = (mode: string) => {
        setViewMode(mode as TimeFrameMode);
        if (mode === "MONTH") onChange(monthTimeFrame(year, month));
        else if (mode === "ACADEMIC_YEAR") onChange(academicYearTimeFrame(academicYearStart));
        // RANGE: wait for an actual selection rather than emitting a guess.
    };

    const handleMonthChange = (nextMonth: number, nextYear: number) => {
        setMonth(nextMonth);
        setYear(nextYear);
        onChange(monthTimeFrame(nextYear, nextMonth));
    };

    const handleAcademicYearChange = (startYear: number) => {
        setAcademicYearStart(startYear);
        onChange(academicYearTimeFrame(startYear));
    };

    const handleRangeSelect = (next: DateRange | undefined) => {
        setRange(next);
        if (!next?.from || !next?.to) {
            setRangeError(undefined);
            return;
        }
        const error = rangeValidationError(next.from, next.to);
        setRangeError(error);
        if (error) return;
        onChange(rangeTimeFrame(next.from, next.to));
    };

    return (
        <div className="flex flex-col gap-2">
            <Tabs value={viewMode} onValueChange={handleTabChange}>
                <TabsList>
                    <TabsTrigger value="MONTH">Month</TabsTrigger>
                    <TabsTrigger value="RANGE">Date range</TabsTrigger>
                    <TabsTrigger value="ACADEMIC_YEAR">Academic year</TabsTrigger>
                </TabsList>

                <TabsContent value="MONTH">
                    <div className="flex gap-3">
                        <Select value={String(month)} onValueChange={(v) => handleMonthChange(Number(v), year)}>
                            <SelectTrigger className="w-40">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {MONTH_NAMES.map((name, i) => (
                                    <SelectItem key={name} value={String(i)}>
                                        {name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={String(year)} onValueChange={(v) => handleMonthChange(month, Number(v))}>
                            <SelectTrigger className="w-28">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {yearOptions().map((y) => (
                                    <SelectItem key={y} value={String(y)}>
                                        {y}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </TabsContent>

                <TabsContent value="RANGE">
                    <Calendar mode="range" selected={range} onSelect={handleRangeSelect} numberOfMonths={2} />
                    {rangeError && (
                        <Text size="sm" className="text-destructive">
                            {rangeError}
                        </Text>
                    )}
                </TabsContent>

                <TabsContent value="ACADEMIC_YEAR">
                    <Select
                        value={String(academicYearStart)}
                        onValueChange={(v) => handleAcademicYearChange(Number(v))}
                    >
                        <SelectTrigger className="w-48">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {yearOptions().map((y) => (
                                <SelectItem key={y} value={String(y)}>
                                    {y}–{y + 1}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </TabsContent>
            </Tabs>
            <Text size="sm" muted>
                Showing: {value.label}
            </Text>
        </div>
    );
}
